import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const NavBar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();

    const triggered = useScrollTrigger({
        disableHysteresis: true,
        threshold: 80,
    });

    const scrollTo = (id: string) => {
        setDrawerOpen(false);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate(`/#${id}`);
        }
    };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    backgroundColor: triggered ? 'rgba(7,11,28,0.95)' : 'transparent',
                    backdropFilter: triggered ? 'blur(12px)' : 'none',
                    transition: 'background-color 0.3s, backdrop-filter 0.3s',
                    borderBottom: triggered ? '1px solid rgba(255,255,255,0.08)' : 'none',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
                    <Typography
                        component={RouterLink}
                        to="/"
                        variant="h6"
                        sx={{
                            fontFamily: '"Cormorant Garamond", serif',
                            fontWeight: 400,
                            fontSize: '1.4rem',
                            letterSpacing: '0.08em',
                            color: '#f0ede8',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Framed Ghosts
                    </Typography>

                    {isMobile ? (
                        <IconButton sx={{ color: '#f0ede8' }} onClick={() => setDrawerOpen(true)}>
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            <Box
                                component="button"
                                onClick={() => scrollTo('about')}
                                sx={{
                                    color: '#9e9e9e',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    p: 0,
                                    '&:hover': { color: '#f0ede8' },
                                    transition: 'color 0.2s',
                                }}
                            >
                                About
                            </Box>
                            <Box
                                component="button"
                                onClick={() => scrollTo('gallery')}
                                sx={{
                                    color: '#9e9e9e',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    p: 0,
                                    '&:hover': { color: '#f0ede8' },
                                    transition: 'color 0.2s',
                                }}
                            >
                                Gallery
                            </Box>
                            <Box
                                component={RouterLink}
                                to="/events"
                                data-testid="nav-events-link"
                                sx={{
                                    color: '#9e9e9e',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    textDecoration: 'none',
                                    '&:hover': { color: '#f0ede8' },
                                    transition: 'color 0.2s',
                                }}
                            >
                                Events
                            </Box>
                            <Box
                                component={RouterLink}
                                to="/contact"
                                data-testid="nav-contact-link"
                                sx={{
                                    color: '#9e9e9e',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    textDecoration: 'none',
                                    '&:hover': { color: '#f0ede8' },
                                    transition: 'color 0.2s',
                                }}
                            >
                                Contact
                            </Box>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                slotProps={{ paper: { sx: { backgroundColor: '#0f1530', width: 220 } } }}
            >
                <List sx={{ pt: 8 }}>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => scrollTo('about')}>
                            <ListItemText primary="About" slotProps={{ primary: { sx: { color: '#f0ede8', fontFamily: '"Inter", sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.9rem' } } }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => scrollTo('gallery')}>
                            <ListItemText primary="Gallery" slotProps={{ primary: { sx: { color: '#f0ede8', fontFamily: '"Inter", sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.9rem' } } }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to="/events"
                            data-testid="drawer-events-link"
                            onClick={() => setDrawerOpen(false)}
                        >
                            <ListItemText primary="Events" slotProps={{ primary: { sx: { color: '#f0ede8', fontFamily: '"Inter", sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.9rem' } } }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to="/contact"
                            data-testid="drawer-contact-link"
                            onClick={() => setDrawerOpen(false)}
                        >
                            <ListItemText primary="Contact" slotProps={{ primary: { sx: { color: '#f0ede8', fontFamily: '"Inter", sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.9rem' } } }} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>
        </>
    );
};

export default NavBar;
